/**
 * Step 4 of /portal/scan: the capture (HANDOFF.md step 5: "Capture with Prism's Web SDK. The video goes from the
 * phone straight to Prism; MCOS never stores it.").
 *
 * Two modes, chosen by the API (lib/scan/prism-flow.ts captureModeFor):
 *
 *   sdk         Prism's web capture (@prismlabs/web-scan-ui-kit). Not integrated: the package is a new dependency
 *               that needs MCOS approval, and how it takes the signed upload URL and which event says the upload
 *               finished are not documented in the pages read. So the API keeps scans switched off on Prism's hosts
 *               (lib/scan/prism-flow.ts prismReadiness, capture_not_wired) and this mode is never handed out. If it
 *               ever is, the page says the camera is not available; it never fakes a capture.
 *   local-mock  Local testing against scripts/dev-prism-mock.ts only (never production, never Prism's hosts): the
 *               API stands in for the phone's upload with a synthetic payload, so the whole flow can be clicked
 *               through on a computer. The screen says so.
 */

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { portalRequest } from '../../lib/portal';
import { Card, DISPLAY_MID, TONE } from './shell';
import { ChatBubble } from './icons';
import { BodyOutline, TurnLine } from './BodyScanIcons';
import type { ScanView } from './BodyScan';

export interface CaptureSession {
  mode: 'sdk' | 'local-mock';
  prismScanId: string;
  uploadUrl: string;
  uploadUrlExpiresAt: string | null;
  assetConfigId: string;
  deviceConfigName: string;
}

const SIMULATED_TURN_MS = 5_000;

function Viewfinder({ progress, recording }: { progress: number; recording: boolean }) {
  const r = 46;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative mx-auto grid aspect-[3/4] w-full max-w-[260px] place-items-center overflow-hidden rounded-[14px]" style={{ background: TONE.row }}>
      <div className="absolute inset-[14px] rounded-[10px] border-2 border-dashed" style={{ borderColor: 'var(--hairline)' }} />
      <BodyOutline className={`relative h-[78%] ${recording ? 'text-forest' : ''}`} />
      {recording && (
        <svg viewBox="0 0 100 100" className="absolute bottom-[10px] right-[10px] h-[54px] w-[54px]" aria-hidden="true">
          <circle cx="50" cy="50" r={r} fill="white" stroke={TONE.line} strokeWidth="6" />
          <circle
            cx="50"
            cy="50"
            r={r}
            fill="none"
            stroke="var(--forest)"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={c}
            strokeDashoffset={c * (1 - progress)}
            transform="rotate(-90 50 50)"
          />
        </svg>
      )}
    </div>
  );
}

export function BodyScanCapture({
  scanId,
  session,
  onCaptured,
  onBack,
}: {
  scanId: string;
  session: CaptureSession;
  onCaptured: (view: ScanView) => void;
  onBack: () => void;
}) {
  if (session.mode === 'local-mock') return <SimulatedCapture scanId={scanId} onCaptured={onCaptured} onBack={onBack} />;
  return <SdkCapture onBack={onBack} />;
}

/** Local testing only: the turn is shown, then the API stands in for the phone's upload to the mock. */
function SimulatedCapture({ scanId, onCaptured, onBack }: { scanId: string; onCaptured: (view: ScanView) => void; onBack: () => void }) {
  const [phase, setPhase] = useState<'idle' | 'recording' | 'sending' | 'error'>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  useEffect(() => () => {
    if (timer.current !== null) window.clearInterval(timer.current);
  }, []);

  const send = async () => {
    setPhase('sending');
    try {
      const view = await portalRequest<ScanView>(`/scan/scans/${scanId}/simulate-capture`, { method: 'POST', body: {} });
      onCaptured(view);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The scan could not be sent.');
      setPhase('error');
    }
  };

  const record = () => {
    setError(null);
    setPhase('recording');
    setProgress(0);
    const started = Date.now();
    timer.current = window.setInterval(() => {
      const p = Math.min(1, (Date.now() - started) / SIMULATED_TURN_MS);
      setProgress(p);
      if (p >= 1) {
        if (timer.current !== null) window.clearInterval(timer.current);
        timer.current = null;
        void send();
      }
    }, 100);
  };

  const busy = phase === 'recording' || phase === 'sending';
  return (
    <Card className="px-[14px] pb-[14px] pt-[13px]" data-testid="scan-capture">
      <h2 className="font-display text-[20px] font-semibold leading-[1.15]" style={{ ...DISPLAY_MID, color: TONE.heading }}>
        {phase === 'recording' ? 'Turn slowly in a circle' : phase === 'sending' ? 'Sending your scan' : 'Stand where your whole body fits'}
      </h2>
      <p className="mt-[5px] text-[13px] leading-relaxed" style={{ color: TONE.body }} aria-live="polite">
        {phase === 'recording'
          ? 'Keep turning until the circle is full.'
          : phase === 'sending'
            ? 'Your scan is on its way to Prism Labs.'
            : 'When you are ready, start recording and turn slowly.'}
      </p>
      <div className="mt-[12px]">
        <Viewfinder progress={progress} recording={phase === 'recording'} />
      </div>
      <p
        className="mt-[12px] rounded-[8px] border border-dashed px-[10px] py-[8px] text-[11.5px] leading-snug"
        style={{ borderColor: 'var(--hairline)', color: TONE.faint }}
        data-testid="scan-test-mode"
      >
        Test mode: this server talks to a local stand-in for Prism Labs, so the camera is not used and no video is
        recorded.
      </p>
      {error && (
        <p className="mt-[10px] text-[12.5px] text-alert" role="alert">
          {error}
        </p>
      )}
      <button
        type="button"
        onClick={record}
        disabled={busy}
        className="mt-[12px] flex h-[44px] w-full items-center justify-center gap-2 rounded-[8px] bg-forest text-[14px] font-semibold text-white transition-colors hover:bg-forest-deep disabled:opacity-60"
        data-testid="scan-record"
      >
        <TurnLine size={17} strokeWidth={2} />
        {phase === 'recording' ? 'Recording' : phase === 'sending' ? 'Sending' : phase === 'error' ? 'Try again' : 'Record my scan'}
      </button>
      <button type="button" onClick={onBack} disabled={busy} className="mt-[8px] w-full py-[6px] text-[12.5px] font-medium disabled:opacity-50" style={{ color: TONE.body }}>
        Back to the tips
      </button>
    </Card>
  );
}

/** Prism's web capture is not integrated yet (see the header), so there is nothing to start: say so plainly. */
function SdkCapture({ onBack }: { onBack: () => void }) {
  return (
    <Card className="px-[14px] pb-[14px] pt-[13px]" data-testid="scan-capture">
      <div className="py-2" role="alert">
        <h2 className="font-display text-[19px] font-semibold leading-[1.15]" style={{ ...DISPLAY_MID, color: TONE.heading }}>
          The scan camera is not available yet
        </h2>
        <p className="mt-[6px] text-[13px] leading-relaxed" style={{ color: TONE.body }}>
          Please try again later. If you need help, your care team is here.
        </p>
        <Link to="/portal/messages/new" className="mt-[12px] flex h-[42px] items-center justify-center gap-[6px] rounded-[8px] bg-forest text-[14px] font-semibold text-white transition-colors hover:bg-forest-deep">
          <ChatBubble size={15} strokeWidth={1.8} />
          Message your care team
        </Link>
      </div>
      <button type="button" onClick={onBack} className="mt-[8px] w-full py-[6px] text-[12.5px] font-medium" style={{ color: TONE.body }}>
        Back to the tips
      </button>
    </Card>
  );
}
